import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useIsAiGeneratedImageSrc } from '../ai-generated'
import { externalCanvasDataAtom } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { Background } from '../data/v1'

export type BackgroundFillProps = {
	background: Background
}

type Size = { w: number; h: number }

function useContainerSize(ref: React.RefObject<HTMLDivElement | null>): Size {
	const [size, setSize] = useState<Size>({ w: 0, h: 0 })

	// Read initial dimensions synchronously before the browser paints
	useLayoutEffect(() => {
		const el = ref.current
		if (!el) { return }
		setSize({ w: el.clientWidth, h: el.clientHeight })
	}, [])

	useEffect(() => {
		const el = ref.current
		if (!el) { return }

		const observer = new ResizeObserver(([entry]) => {
			setSize({ w: entry.contentRect.width, h: entry.contentRect.height })
		})
		observer.observe(el)
		return () => observer.disconnect()
	}, [])

	return size
}

type BackgroundImageProps = {
	src: string
	imageScale: number
	imageOffsetX: number
	imageOffsetY: number
}

function BackgroundImage({ src, imageScale, imageOffsetX, imageOffsetY }: BackgroundImageProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const containerSize = useContainerSize(containerRef)
	const [imgSize, setImgSize] = useState<Size | null>(null)
	const isAiGenerated = useIsAiGeneratedImageSrc(src)

	const onLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
		const img = e.currentTarget
		setImgSize({ w: img.naturalWidth, h: img.naturalHeight })
	}, [])

	const nativeScale = imgSize && containerSize.w > 0 && containerSize.h > 0
		? Math.max(containerSize.w / imgSize.w, containerSize.h / imgSize.h)
		: null

	const userScale = imageScale / 100
	const totalScale = nativeScale ? nativeScale * userScale : null

	let panX = 0
	let panY = 0
	if (totalScale && imgSize) {
		const scaledW = imgSize.w * totalScale
		const scaledH = imgSize.h * totalScale
		const clampedOffsetX = Math.min(Math.max(imageOffsetX, 0), 100)
		const clampedOffsetY = Math.min(Math.max(imageOffsetY, 0), 100)
		panX = (0.5 - clampedOffsetX / 100) * (scaledW - containerSize.w)
		panY = (0.5 - clampedOffsetY / 100) * (scaledH - containerSize.h)
	}

	return (
		<div className="absolute inset-0 overflow-hidden" ref={containerRef}>
			<div
				style={totalScale && imgSize
					? {
						transform: `translate(-50%, -50%) translate(${panX}px, ${panY}px)`,
						position: 'absolute',
						top: '50%',
						left: '50%',
						maxWidth: 'none',
					}
					: undefined}
			>
				<img
					src={src}
					alt=""
					draggable={false}
					onLoad={onLoad}
					data-ai-generated={isAiGenerated ? 'true' : undefined}
					width={imgSize?.w}
					height={imgSize?.h}
					style={{
						maxWidth: 'none',
						transform: totalScale ? `scale(${totalScale})` : `scale(${nativeScale})`,
					}}
				/>
			</div>
		</div>
	)
}

export function BackgroundFill({ background }: BackgroundFillProps) {
	const store = useCanvasStore()
	const externalData = useAtomValue(externalCanvasDataAtom, { store })

	switch (background.mode) {
		case 'transparent':
			return null
		case 'foregroundColor':
			return <div className="absolute inset-0" style={{ backgroundColor: externalData.foregroundColor }} />
		case 'backgroundColor':
			return <div className="absolute inset-0" style={{ backgroundColor: externalData.backgroundColor }} />
		case 'accentForegroundColor':
			return <div className="absolute inset-0" style={{ backgroundColor: externalData.accentForegroundColor }} />
		case 'accentBackgroundColor':
			return <div className="absolute inset-0" style={{ backgroundColor: externalData.accentBackgroundColor }} />
		case 'color':
			return <div className="absolute inset-0" style={{ backgroundColor: background.color }} />
		case 'image':
			return background.imageUrl
				? (
					<BackgroundImage
						src={background.imageUrl}
						imageScale={background.imageScale}
						imageOffsetX={background.imageOffsetX}
						imageOffsetY={background.imageOffsetY}
					/>
				)
				: null
		case 'externalImage':
			return externalData.backgroundUrl
				? (
					<BackgroundImage
						src={externalData.backgroundUrl}
						imageScale={background.imageScale}
						imageOffsetX={background.imageOffsetX}
						imageOffsetY={background.imageOffsetY}
					/>
				)
				: null
	}
}
